import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";

/**
 * Service for handling file upload operations
 */
const FileUploadService = {
  /**
   * Get the file type from a filename
   *
   * @param {string} fileName - The name of the file
   * @returns {string} The type of the file
   */
  getFileType(fileName) {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "ifc":
        return "ifc";
      case "rvt":
        return "rvt";
      case "pdf":
        return "pdf";
      case "xlsx":
        return "xlsx";
      case "zip":
        return "zip";
      default:
        return "other";
    }
  },

  /**
   * Upload a file to the server
   *
   * @param {File} file - The file to upload
   * @param {string} projectID - The project ID
   * @param {Function} setProgress - Function to update upload progress
   * @param {boolean} isEpcFile - Whether the file is an EPC certificate
   * @returns {Promise<Object>} The upload result
   */
  async uploadFile(file, projectID, setProgress, isEpcFile = false) {
    try {
      if (isEpcFile) {
        return this.uploadEPCFile(file, projectID, setProgress);
      }

      setProgress((prev) => ({
        ...prev,
        [file.name]: "Creating floor layer record...",
      }));

      const floorLayerData = {
        project: projectID,
        name: file.name,
        type: this.getFileType(file.name),
      };

      const floorLayerResponse = await apiClient.post(
        "/api/floor_interaction/",
        floorLayerData,
        { showToast: false }
      );

      if (!floorLayerResponse || !floorLayerResponse.id) {
        throw new Error("Failed to create floor layer record");
      }

      setProgress((prev) => ({
        ...prev,
        [file.name]: "Uploading file...",
      }));

      const formData = new FormData();
      formData.append("layer_file", file);
      formData.append("floors_layer", floorLayerResponse.id);

      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/api/floor_file_interaction/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      setProgress((prev) => ({
        ...prev,
        [file.name]: "Complete",
      }));

      toast.success(`Successfully uploaded ${file.name}`);
      return { success: true };
    } catch (error) {
      console.error("Upload error:", error);
      setProgress((prev) => ({
        ...prev,
        [file.name]: "Failed",
      }));
      toast.error(`Failed to upload ${file.name}`);
      return { success: false, error };
    }
  },

  /**
   * Upload EPC certificate file
   *
   * @param {File} file - The EPC PDF file to upload
   * @param {string} projectID - The project ID
   * @param {Function} setProgress - Function to update upload progress
   * @returns {Promise<Object>} The upload result
   */
  async uploadEPCFile(file, projectID, setProgress) {
    try {
      setProgress((prev) => ({
        ...prev,
        [file.name]: "Uploading EPC certificate...",
      }));

      const formData = new FormData();
      formData.append("pdf_file", file);
      formData.append("project", projectID);

      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/api/europe_file/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`EPC upload failed with status: ${response.status}`);
      }

      setProgress((prev) => ({
        ...prev,
        [file.name]: "Complete",
      }));

      toast.success(`Successfully uploaded EPC certificate: ${file.name}`);
      return { success: true };
    } catch (error) {
      console.error("EPC upload error:", error);
      setProgress((prev) => ({
        ...prev,
        [file.name]: "Failed",
      }));
      toast.error(`Failed to upload EPC certificate: ${file.name}`);
      return { success: false, error };
    }
  },

  /**
   * Delete files from the server
   *
   * @param {Array} fileIds - Array of file IDs to delete
   * @returns {Promise<Object>} The deletion result
   */
  async deleteFiles(fileIds) {
    const failedDeletes = [];

    try {
      for (const fileId of fileIds) {
        try {
          await apiClient.delete(`/api/floor_interaction/${fileId}/`, {
            showToast: false,
          });
        } catch (error) {
          console.error(`Failed to delete file ${fileId}:`, error);
          failedDeletes.push(fileId);
        }
      }

      if (failedDeletes.length === 0) {
        toast.success("Files deleted successfully");
        return { success: true };
      } else {
        const errorMessage = `Failed to delete ${failedDeletes.length} files`;
        toast.error(errorMessage);
        return { success: false, error: new Error(errorMessage) };
      }
    } catch (error) {
      toast.error("Failed to delete files");
      return { success: false, error };
    }
  },

  /**
   * Fetch files from the server
   *
   * @param {string} projectID - The project ID
   * @returns {Promise<Array>} Array of files
   */
  async fetchFiles(projectID) {
    try {
      return await apiClient.get(
        "/api/layers/get_all_floors_plan_of_floors_layer/",
        {
          params: { project_id: projectID },
          showToast: false,
        }
      );
    } catch (error) {
      toast.error("Failed to load files");
      throw error;
    }
  },
};

export default FileUploadService;
